import React, { useContext } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  CREATOR_AND_UP,
  GUEST,
  hasExternalCreator,
  hasInternalCreator,
} from "common/authorization";
import { AuthContext } from "../../../../../Authorization/AuthContext";
import { Title } from "../../../../../Common/Typography";
import { TagSelect as TagSelectNonForm } from "../../../../../Inputs";
import { Label } from "../../../../../Inputs";
import { Select, Text, Textarea } from "../../../../../Inputs/react-hook-form";
import { TagSelect } from "../../../../../Inputs/react-hook-form";
import { RichText } from "../../../../../Inputs/RichText";
import Wrapper from "../../../../../Inputs/Wrapper";
import { SUBMISSION_CATEGORY_OPTIONS } from "../../../../../Submissions/utils";
import { editorTypeOptions, viewerTypeOptions } from "../constants";
import { SubmissionsBuilderContextInstance } from "../context";
import { onCanCreateChange } from "../helpers";
import { RoleAndGroupSelector } from "../RoleAndGroupSelector";
import { SubmissionsBuilderFormDataStructure } from "../types";
import {
  submissionHiddenFromPublicByDefaultToViewerType,
  useIsSisdAndHidden,
  viewerTypeToSubmissionHiddenFromPublicByDefault,
} from "../utils";
import {
  FieldWrapper,
  FormSection,
  FormSectionTitleWrapper,
} from "./__styles__/SidebarColumn";
import AttachmentSelect from "./AttachmentSelect";

const FormSettingsTab = () => {
  const { account } = useContext(AuthContext);
  const {
    readOnly,
    setCanCreateGroups,
    canCreateGroups,
    canCreateRoles,
    setCanCreateRoles,
  } = useContext(SubmissionsBuilderContextInstance);
  const {
    control,
    formState: { defaultValues, errors },
    getValues,
    register,
    setValue,
    watch,
  } = useFormContext<SubmissionsBuilderFormDataStructure>();

  const accountUserGroups = account?.userGroups ?? [];

  const modules = watch("modules");
  const visibilityDisabled = useIsSisdAndHidden(modules);

  const visibilityShown = account?.publicPortal.enabled;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {!readOnly && (
        <div>
          <Label text={"All fields are required"} required />
        </div>
      )}

      <Text
        {...register("name", { required: "Record type is required" })}
        label="Record type"
        error={errors.name?.message}
        disabled={readOnly}
      />
      <div>
        <Label text="Record category" htmlFor="category" />
        <Select
          disabled={!!defaultValues?.inputs?.length || readOnly}
          control={control}
          name="category"
          options={SUBMISSION_CATEGORY_OPTIONS}
          size="medium"
          rules={{ required: "Record category is required" }}
        />
      </div>

      <FormSection>
        <FormSectionTitleWrapper>
          <Title size="small" type="semiBold">
            Permissions and defaults
          </Title>
        </FormSectionTitleWrapper>
        <FieldWrapper>
          <RoleAndGroupSelector
            control={control}
            options={{
              roles: [...CREATOR_AND_UP, ...GUEST],
              userGroups: accountUserGroups,
            }}
            name="canCreateRolesAndGroupIds"
            error={errors.canCreateRolesAndGroupIds?.message}
            label="Who can fill out this form?"
            disabled={readOnly}
            onChange={() =>
              onCanCreateChange({
                getFormData: getValues,
                setFormData: setValue,
                accountUserGroups,
                setCanCreateGroups,
                setCanCreateRoles,
              })
            }
            data-testid="creator-permissions"
          />
        </FieldWrapper>
        {visibilityShown && (
          <FieldWrapper>
            <TagSelect
              control={control}
              name="hiddenFromPublicByDefault"
              options={viewerTypeOptions}
              rules={{ required: "This field is required" }}
              error={errors.hiddenFromPublicByDefault?.message}
              disabled={visibilityDisabled || readOnly}
              label="Who can view this form after it is submitted?"
              iconName="eye"
              isMulti={false}
              transform={{
                output: viewerTypeToSubmissionHiddenFromPublicByDefault,
                input: submissionHiddenFromPublicByDefaultToViewerType,
              }}
              data-testid="viewer-permissions"
            />
          </FieldWrapper>
        )}
        {/* unconnected to react-hook-form/ read-only for now */}
        <FieldWrapper>
          <Wrapper
            name="editorType"
            label="Who can edit this form after it is submitted?"
          >
            <TagSelectNonForm
              name="editorType"
              options={editorTypeOptions}
              value={editorTypeOptions[0]!.value}
              disabled={true}
              iconName="pencil"
              isMulti={false}
              onChange={() => {}}
              data-testid="editor-permissions"
            />
          </Wrapper>
        </FieldWrapper>
      </FormSection>

      <AttachmentSelect disabled={readOnly} />

      <div>
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState }) => (
            <RichText
              name={field.name}
              label="Form description"
              value={field.value || ""}
              onChange={field.onChange}
              error={fieldState.error?.message}
              disabled={readOnly}
            />
          )}
        />
      </div>
      {hasInternalCreator({ canCreateRoles, canCreateGroups }) && (
        <div>
          <Label text={"Success message"} htmlFor="successMessage" />
          <Text
            {...register("successMessage", {
              required: "Success message is required",
            })}
            error={errors.successMessage?.message}
            disabled={readOnly}
          />
        </div>
      )}
      {hasExternalCreator({ canCreateRoles, canCreateGroups }) && (
        <>
          <div>
            <Label text="Success page title" htmlFor="preamble" />
            <Text
              {...register("preamble", {
                required: "Preamble is required",
              })}
              error={errors.preamble?.message}
              disabled={readOnly}
            />
          </div>
          <div>
            <Label text="Success page message" htmlFor="postamble" />
            <Text
              {...register("postamble", {
                required: "Postamble is required",
              })}
              error={errors.postamble?.message}
              disabled={readOnly}
            />
          </div>
          <div>
            <Label
              text="Notification emails"
              htmlFor="notificationEmails"
              tooltip="Please enter each email on its own line"
            />
            <Textarea
              {...register("notificationEmails")}
              error={errors.notificationEmails?.message}
              data-testid="notification-emails"
              disabled={readOnly}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default FormSettingsTab;
